import { FormAdapter, FormContainerOptions, FormControlProperties, FormControlType, FormValues } from '@omegagrid/form';
import { msg } from "@omegagrid/localize";
import rp, { listKeys } from '@reporting/rp-app-host';
import { Accounting, AccountingRights } from '@reporting/rp-types/core';
import { Project } from '@reporting/rp-types/ext';

/**
 * Copies a project into other companies - or into this one as a duplicate - without registering it
 * for synchronization: a one-off copy, the way v1 offered it. Every chosen company gets one copy,
 * under the given name or the project's own.
 */
export class ProjectCopyFormAdapter extends FormAdapter {

	private project: Project;
	private companies: Map<number, string>;

	constructor(readonly id: number) { super(); }

	getColumns() {
		return [
			{ width: 150 },
			{ width: undefined },
		];
	}

	async getControls(): Promise<FormControlProperties[]> {
		this.project ??= await rp.ms('extensions').get<Project>(`projects/${this.id}`);
		// the companies the user may define custom projects in - the service refuses the others
		const accountings = await rp.ms('accounting').get<Accounting[]>('accountings', { right: AccountingRights.CustomDefine, order: ['ACCOUNTING'] });
		this.companies = new Map(accountings.map(a => [a.accounting, `${a.accounting} ${a.company}`]));
		return [
			{ row: 0, col: 0, type: FormControlType.Label, value: msg('Target company'), for: 'accountings' },
			{ row: 0, col: 1, type: FormControlType.List, key: 'accountings', value: null, required: true, content: {
				sourceData: [...this.companies].map(([k, v]) => ({ k, v })),
				options: { selectMode: 'multiple', search: true }
			}},
			{ row: 1, col: 0, type: FormControlType.Label, value: msg('Name'), for: 'name' },
			{ row: 1, col: 1, type: FormControlType.TextInput, key: 'name', value: this.project.name },
		];
	}

	getOptions(): FormContainerOptions {
		return {
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: ["save", "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean> {
		const formValues = values.get(0);
		const accountings = listKeys(formValues.accountings).map(Number);
		// the list validates nothing itself; a thrown error is what keeps the form open with the message
		if (!accountings.length) throw new Error(msg('Select a target company'));
		const name = String(formValues.name ?? '').trim();

		const copies = await rp.ms('extensions').post<Project[]>(`projects/${this.id}/copy`, { accountings, name });
		rp.host.showAlert({
			type: 'success',
			title: msg('Copy project'),
			content: `${msg('Copied to')}: ${copies.map(c => `${this.companies.get(c.accounting) ?? c.accounting} (#${c.id_custom_page})`).join(', ')}`,
			timeout: 6000,
		});
		return true;
	}

}
